import { useState, useEffect, useRef } from 'react';
import { useLocation } from 'wouter';
import api from '../api';
import { useAuth, useToasts } from '../state';

/**
 * Floating feedback widget. A small fixed pill in the bottom-right that opens
 * a styled panel for free-form feedback. Works for everyone:
 *  - Signed-in users: attributed to their account.
 *  - Anonymous visitors: stored with no user + an optional contact email.
 *
 * Saves via the (now anonymous-capable) `submitFeedback` method, tagging the
 * page it was sent from. Self-contained styling so it can mount globally.
 *
 * On session chat pages it shows on desktop (in the left sidebar gutter, clear
 * of the centered composer) but hides on mobile, where the composer is
 * full-width and would collide. Fully hidden on the anonymous /try funnel.
 */
// The anonymous /try funnel stays deliberately minimal (its own composer).
// Guided demos (/demos/*, /pathway/demo, /design-sketch/demo) have their own guide card in that corner.
// Hidden on shared memo links too: that page is for someone outside EntreDash, with no app chrome.
const HIDDEN_ON = (path: string) => path === '/try' || path.startsWith('/demos/') || path.endsWith('/demo') || path.startsWith('/shared/') || path.startsWith('/witness/') || path.startsWith('/merge-accounts/');

// Authenticated session chats (patent strategy, needs discovery, business case)
// have a bottom-fixed composer. On desktop the pill lives in the left sidebar
// gutter, well clear of the centered composer, so it shows normally there. On
// mobile the composer spans the full width at the bottom, so on those screens
// the pill is hidden (see the media query) rather than sit on the Send button.
const IS_SESSION = (path: string) =>
  path.endsWith('/session') ||
  path.endsWith('/needs-discovery') ||
  path.endsWith('/business-case') ||
  // The Invention Log's and the packet's phone docks sit where the floating button would.
  path.endsWith('/log') ||
  path.endsWith('/packet');

const TYPES = [
  { id: 'general', label: 'General' },
  { id: 'bug', label: 'Bug' },
  { id: 'idea', label: 'Idea' },
] as const;

type FeedbackType = (typeof TYPES)[number]['id'];

export function FeedbackWidget() {
  const [location] = useLocation();
  const user = useAuth((s) => s.user);
  const showToast = useToasts((s) => s.show);

  const [open, setOpen] = useState(false);
  const [type, setType] = useState<FeedbackType>('general');
  const [message, setMessage] = useState('');
  const [email, setEmail] = useState('');
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const panelRef = useRef<HTMLDivElement | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);

  // The desktop rail's "Feedback" row opens the same panel (the floating button is hidden there).
  useEffect(() => {
    const onOpen = () => setOpen(true);
    window.addEventListener('ed:open-feedback', onOpen);
    return () => window.removeEventListener('ed:open-feedback', onOpen);
  }, []);

  // Close on Escape; close on outside click.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    const onClick = (e: MouseEvent) => {
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('keydown', onKey);
    // Defer outside-click binding so the opening click doesn't immediately close it.
    const t = setTimeout(() => document.addEventListener('mousedown', onClick), 0);
    return () => {
      document.removeEventListener('keydown', onKey);
      clearTimeout(t);
      document.removeEventListener('mousedown', onClick);
    };
  }, [open]);

  // Focus the textarea when the panel opens.
  useEffect(() => {
    if (open && !sent) {
      const t = setTimeout(() => textareaRef.current?.focus(), 120);
      return () => clearTimeout(t);
    }
  }, [open, sent]);

  // Phones: tuck the button away while the reader scrolls down a long page,
  // and bring it back on scroll up or near the top, so it never rests on
  // the text being read.
  useEffect(() => {
    const last = new WeakMap<object, number>();
    let idle: number | undefined;
    const root = document.documentElement;
    const onScroll = (e: Event) => {
      if (!window.matchMedia('(max-width: 767px)').matches) return;
      const t = e.target as HTMLElement | Document;
      const el = t === document ? document.scrollingElement ?? root : (t as HTMLElement);
      const top = (el as HTMLElement).scrollTop ?? 0;
      const prev = last.get(el) ?? 0;
      if (Math.abs(top - prev) < 6) return;
      last.set(el, top);
      root.classList.toggle('fbw-tucked', top > prev && top > 120);
      // On long reading pages, also tuck it away once scrolling settles; it
      // returns on the next scroll up.
      window.clearTimeout(idle);
      if (top > 120) idle = window.setTimeout(() => root.classList.add('fbw-tucked'), 2200);
    };
    window.addEventListener('scroll', onScroll, { capture: true, passive: true });
    return () => {
      window.removeEventListener('scroll', onScroll, { capture: true } as EventListenerOptions);
      window.clearTimeout(idle);
      root.classList.remove('fbw-tucked');
    };
  }, []);

  if (HIDDEN_ON(location)) return null;

  // Bottom-right on the auth pages (their form lives on the right, dark
  // brand panel on the left) AND on the landing hero, whose ENTIRE CTA stack
  // (input, "Get my verdict", the "Try Spark" pill, trust + pricing lines)
  // lives in the LEFT column — a bottom-left FAB there sat directly on top of
  // the "Try Spark" pill. The landing's right side is the decorative product
  // showcase, so the corner is clear there. Bottom-left everywhere else, where
  // marketing content leads on the left.
  const onRight =
    location === '/signin' ||
    location === '/signup' ||
    location === '/' ||
    location === '/landing' ||
    // Admin: the section list runs down the left edge, and the last item sat
    // under the pill.
    location.startsWith('/admin');

  const handleSend = async () => {
    if (!message.trim()) {
      showToast('Type your feedback before sending.', 'error');
      return;
    }
    setSending(true);
    try {
      await api.submitFeedback({
        type,
        message: message.trim(),
        email: !user && email.trim() ? email.trim() : undefined,
        pagePath: location,
      });
      setSent(true);
      setMessage('');
      setEmail('');
      // Auto-close shortly after the thank-you so it doesn't linger.
      setTimeout(() => {
        setOpen(false);
        // Reset to the form after the close animation.
        setTimeout(() => setSent(false), 300);
      }, 1700);
    } catch (e: any) {
      showToast(e?.message ?? 'Could not send feedback. Try again.', 'error');
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <style>{FEEDBACK_CSS}</style>
      <div className={`fbw${onRight ? ' fbw--right' : ''}${IS_SESSION(location) ? ' fbw--session' : ''}${open ? ' is-open' : ''}`}>
        {open && (
          <div
            ref={panelRef}
            className="fbw__panel"
            role="dialog"
            aria-label="Send feedback"
          >
            {sent ? (
              <div className="fbw__thanks">
                <div className="fbw__check" aria-hidden="true">
                  <svg viewBox="0 0 24 24" width="22" height="22" fill="none">
                    <path
                      d="M20 6L9 17l-5-5"
                      stroke="currentColor"
                      strokeWidth="2.4"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>
                <p className="fbw__thanks-title">Thank you.</p>
                <p className="fbw__thanks-sub">
                  Your feedback goes straight to the team.
                </p>
              </div>
            ) : (
              <>
                <div className="fbw__head">
                  <span className="fbw__title">Share feedback</span>
                  <button
                    className="fbw__close"
                    onClick={() => setOpen(false)}
                    aria-label="Close feedback"
                  >
                    <svg viewBox="0 0 24 24" width="16" height="16" fill="none">
                      <path
                        d="M6 6l12 12M18 6L6 18"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                      />
                    </svg>
                  </button>
                </div>

                <div className="fbw__types">
                  {TYPES.map((opt) => (
                    <button
                      key={opt.id}
                      className={`fbw__type ${type === opt.id ? 'is-active' : ''}`}
                      onClick={() => setType(opt.id)}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>

                <textarea
                  ref={textareaRef}
                  className="fbw__textarea"
                  placeholder="What's working, what's broken, what you wish it could do..."
                  value={message}
                  maxLength={4000}
                  onChange={(e) => setMessage(e.target.value)}
                />

                {!user && (
                  <input
                    className="fbw__email"
                    type="email"
                    placeholder="Email (optional, if you'd like a reply)"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                  />
                )}
                {/* Signed in: no email box, since feedback is already linked to
                    the account. Say so, so it doesn't look missing. */}
                {user && (user.email || user.phone) && (
                  <p className="fbw__replyto">
                    We&apos;ll reply to you at <strong>{user.email || user.phone}</strong>.
                  </p>
                )}

                <button
                  className="fbw__send"
                  onClick={handleSend}
                  disabled={sending}
                >
                  {sending ? (
                    <span className="fbw__spinner" aria-label="Sending" />
                  ) : (
                    'Send feedback'
                  )}
                </button>
              </>
            )}
          </div>
        )}

        <button
          className={`fbw__fab ${open ? 'is-open' : ''}`}
          onClick={() => setOpen((v) => !v)}
          aria-label={open ? 'Close feedback' : 'Send feedback'}
          aria-expanded={open}
        >
          <svg viewBox="0 0 24 24" width="18" height="18" fill="none" aria-hidden="true">
            <path
              d="M21 11.5a8.38 8.38 0 0 1-8.5 8.5 8.5 8.5 0 0 1-3.8-.9L3 21l1.9-5.7a8.5 8.5 0 0 1-.9-3.8A8.38 8.38 0 0 1 12.5 3 8.38 8.38 0 0 1 21 11.5z"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          <span className="fbw__fab-label">Feedback</span>
        </button>
      </div>
    </>
  );
}

const FEEDBACK_CSS = `
  /* Hidden while the Spark modal (homepage phone-frame takeover) is open — the
     FAB belongs to the page behind and must not float over the focused funnel. */
  html.spark-modal-open .fbw { display: none !important; }
  .fbw {
    position: fixed;
    /* Align to the centered ~1440 container's left edge rather than the bare
       viewport edge, so on wide 16:9 monitors the pill stays locked in line
       with the hero card instead of drifting out to the display bezel.
       Collapses to a 24px gutter on normal/narrow screens. */
    left: max(24px, calc(50vw - 720px));
    bottom: 24px;
    z-index: 1200;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
    user-select: none;
  }
  /* Auth pages: anchor bottom-right instead (form lives on the right there),
     aligned to the same container edge. */
  .fbw--right {
    left: auto;
    right: max(24px, calc(50vw - 720px));
    align-items: flex-end;
  }
  /* Recessive by default — Parchment fill, hairline, Ink text — so the
     floating pill stays unobtrusive and doesn't compete with the real CTAs
     (which own solid Heartwood). Warms to Heartwood only on hover. */
  .fbw__fab {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 11px 18px 11px 15px;
    border: 1px solid var(--border-hairline, rgba(15,23,20,0.14));
    border-radius: var(--r-pill, 999px);
    background: var(--surface-card, #fbf8f0);
    color: var(--ink, #0f1714);
    font-family: var(--f-sans, inherit);
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    box-shadow: 0 1px 2px rgba(15,23,20,0.05), 0 6px 18px rgba(15,23,20,0.10);
    transition: transform 160ms ease, box-shadow 160ms ease, opacity 160ms ease,
      background 160ms ease, color 160ms ease, border-color 160ms ease;
  }
  .fbw__fab:hover {
    transform: translateY(-1px);
    background: var(--brass, #b5853c);
    color: var(--ink, #0f1714);
    border-color: var(--brass, #b5853c);
    box-shadow: 0 3px 8px rgba(15,23,20,0.16), 0 12px 30px rgba(15,23,20,0.20);
  }
  .fbw__fab.is-open { opacity: 0; pointer-events: none; }
  .fbw__fab svg { flex-shrink: 0; }

  .fbw__panel {
    width: 340px;
    max-width: calc(100vw - 32px);
    background: var(--surface-card, #fbf8f0);
    border: 1px solid var(--border-hairline, rgba(15,23,20,0.12));
    border-radius: 16px;
    box-shadow: 0 4px 12px rgba(15,23,20,0.10), 0 24px 60px rgba(15,23,20,0.20);
    padding: 18px;
    animation: fbw-in 220ms cubic-bezier(0.16, 1, 0.3, 1);
  }
  @keyframes fbw-in {
    from { opacity: 0; transform: translateY(8px) scale(0.98); }
    to { opacity: 1; transform: translateY(0) scale(1); }
  }
  .fbw__head { display: flex; align-items: center; justify-content: space-between; margin-bottom: 14px; }
  .fbw__title { font-family: var(--f-serif, serif); font-size: 17px; font-weight: 500; color: var(--ink, #0f1714); }
  .fbw__close {
    display: inline-flex; align-items: center; justify-content: center;
    width: 28px; height: 28px; border: none; border-radius: 8px;
    background: transparent; color: var(--text-decorative, #6b6b6b); cursor: pointer;
    transition: background 140ms ease;
  }
  .fbw__close:hover { background: rgba(15,23,20,0.06); }

  .fbw__types { display: flex; gap: 8px; margin-bottom: 12px; }
  .fbw__type {
    flex: 1; padding: 8px 0; border-radius: var(--r-pill, 999px);
    border: 1px solid var(--border-hairline, rgba(15,23,20,0.14));
    background: transparent; color: var(--text-decorative, #6b6b6b);
    font-family: var(--f-mono, monospace); font-size: 11px; letter-spacing: 0.04em;
    text-transform: uppercase; cursor: pointer;
    transition: background 140ms ease, color 140ms ease, border-color 140ms ease;
  }
  .fbw__type.is-active {
    background: rgba(181,133,60,0.12);
    border-color: var(--brass, #b5853c);
    color: var(--brass-deep, #a37828);
    font-weight: 700;
  }

  .fbw__textarea, .fbw__email {
    width: 100%;
    box-sizing: border-box;
    border: 1px solid var(--border-hairline, rgba(15,23,20,0.14));
    border-radius: 10px;
    background: var(--surface-page, #f4efe2);
    color: var(--ink, #0f1714);
    font-family: var(--f-sans, inherit);
    font-size: 14px;
    padding: 11px 12px;
    transition: border-color 140ms ease, box-shadow 140ms ease;
  }
  .fbw__textarea {
    min-height: 96px; resize: vertical; line-height: 1.5; margin-bottom: 10px;
  }
  .fbw__textarea::placeholder, .fbw__email::placeholder { color: var(--text-decorative, #9a958b); }
  .fbw__textarea:focus, .fbw__email:focus {
    outline: none;
    border-color: var(--brass, #b5853c);
    box-shadow: 0 0 0 3px color-mix(in srgb, var(--brass, #b5853c) 20%, transparent);
  }
  .fbw__email { margin-bottom: 12px; }
  .fbw__replyto { margin: -2px 0 12px; font-size: 12.5px; line-height: 1.45; color: var(--text-decorative, #6b6b6b); overflow-wrap: anywhere; }
  .fbw__replyto strong { color: var(--ink, #0f1714); font-weight: 600; }

  .fbw__send {
    width: 100%; padding: 12px 0; border: none; border-radius: var(--r-pill, 999px);
    background: var(--heartwood, #1b3a2e); color: var(--parchment, #f4efe2);
    font-family: var(--f-sans, inherit); font-size: 14px; font-weight: 600;
    cursor: pointer; min-height: 44px;
    display: inline-flex; align-items: center; justify-content: center;
    transition: opacity 140ms ease, transform 140ms ease;
  }
  .fbw__send:hover:not(:disabled) { transform: translateY(-1px); }
  .fbw__send:disabled { opacity: 0.75; cursor: default; }
  .fbw__spinner {
    width: 16px; height: 16px; border-radius: 50%;
    border: 2px solid color-mix(in srgb, var(--parchment, #f4efe2) 45%, transparent);
    border-top-color: var(--parchment, #f4efe2);
    animation: fbw-spin 0.7s linear infinite;
  }
  @keyframes fbw-spin { to { transform: rotate(360deg); } }

  .fbw__thanks { text-align: center; padding: 14px 6px 8px; }
  .fbw__check {
    width: 44px; height: 44px; margin: 0 auto 12px;
    border-radius: 50%; display: flex; align-items: center; justify-content: center;
    background: rgba(40,72,52,0.1); color: var(--heartwood, #1b3a2e);
    animation: fbw-pop 320ms cubic-bezier(0.16, 1, 0.3, 1);
  }
  @keyframes fbw-pop { from { transform: scale(0.6); opacity: 0; } to { transform: scale(1); opacity: 1; } }
  .fbw__thanks-title { font-family: var(--f-serif, serif); font-size: 18px; color: var(--ink, #0f1714); margin: 0 0 4px; }
  .fbw__thanks-sub { font-size: 13px; color: var(--text-decorative, #6b6b6b); margin: 0; line-height: 1.5; }

  @media (max-width: 600px) {
    /* On phones, anchor the pill to the bottom-RIGHT (the conventional FAB
       spot) instead of bottom-left. The page content is LEFT-aligned (step
       numbers "01/02/03", headings, body), so a bottom-left button sat on top
       of that reading content as it scrolled past — it was reported overlapping
       the "03 / Get a verdict" label. Bottom-right keeps it clear of the start
       of every line. It's already collapsed to an icon-only circle here. */
    .fbw {
      left: auto;
      right: 16px;
      bottom: 16px;
      align-items: flex-end;
    }
    .fbw--right { left: auto; right: 16px; }
    .fbw__fab-label { display: none; }
    .fbw__fab { padding: 13px; }
    /* Session chats have a full-width bottom composer on phones — the FAB would
       sit on the Send button, so it's hidden here (still reachable on desktop,
       and via Settings). */
    .fbw--session { display: none; }
  }
  @media (max-width: 767px) {
    .fbw { transition: opacity 180ms ease, transform 180ms ease; }
    html.fbw-tucked .fbw:not(.is-open) { opacity: 0; transform: translateY(12px); pointer-events: none; }
  }
  @media (prefers-reduced-motion: reduce) {
    .fbw__panel, .fbw__check { animation: none; }
    .fbw__fab { transition: none; }
  }
`;
